'use client';

import Link from 'next/link';
import { ThemeAwareLogo } from '@/components/common/ThemeAwareLogo';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { ThemeSlider } from './ThemeSlider';

type FooterVariant = 'app' | 'static';

export interface FooterClientProperties {
  hideOnMobile?: boolean;
  isAuthenticated?: boolean;
  variant?: FooterVariant;
}

export function FooterClient({
  variant = 'app',
  isAuthenticated: isAuthenticatedProperty,
  hideOnMobile = false,
}: FooterClientProperties) {
  const { isAuthenticated: hookIsAuthenticated } = useAuth();
  const isAuthenticated = isAuthenticatedProperty ?? hookIsAuthenticated;
  const currentYear = 2025;
  // biome-ignore lint/style/noProcessEnv: Next inlines NODE_ENV; keep prefetch on in prod only (Turbopack dev can throw "module factory is not available" during background prefetch).
  const shouldPrefetchMarketingPages = process.env.NODE_ENV === 'production';

  const footerShellClassName = cn(
    variant === 'static'
      ? 'mt-auto border-t border-border/60 bg-background/90 backdrop-blur'
      : 'mt-auto border-t app-chrome-surface app-chrome-shadow-top',
    hideOnMobile && 'hidden sm:block'
  );

  return (
    <footer className={footerShellClassName}>
      <div className="container flex w-full flex-col gap-4 py-4 text-xs text-muted-foreground/80 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-4">
          <Link href={isAuthenticated ? '/timeline' : '/'} className="flex items-center" aria-label="Sweetistics home">
            <ThemeAwareLogo
              alt="Sweetistics"
              lightSrc="/assets/logo-text.png"
              darkSrc="/assets/logo-text-white.png"
              width={160}
              height={32}
              className="h-6 w-auto"
            />
          </Link>

          {!isAuthenticated && (
            <div className="flex items-center gap-1.5">
              <span>©</span>
              <span>{currentYear}</span>
              <span>Amantus Machina GmbH</span>
            </div>
          )}
        </div>

        <div className="flex flex-col items-center gap-3 sm:flex-row sm:items-center sm:gap-6">
          <nav className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 sm:h-10 sm:justify-end">
            <Link href="/terms" className="hover:text-foreground">
              Terms
            </Link>
            <Link href="/privacy" className="hover:text-foreground">
              Privacy
            </Link>
            <Link href="/pricing" prefetch={shouldPrefetchMarketingPages} className="hover:text-foreground">
              Pricing
            </Link>
            <a
              href="https://status.sweetistics.com"
              className="hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
            >
              Status
            </a>
            <a
              href="https://roadmap.sweetistics.com/docs"
              className="hover:text-foreground"
              target="_blank"
              rel="noopener noreferrer"
            >
              Docs
            </a>
            <Link href="/contact" className="hover:text-foreground">
              Contact
            </Link>
          </nav>
          <ThemeSlider className="shrink-0" />
        </div>
      </div>
    </footer>
  );
}
